(function () {
  'use strict';

  const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency', currency: window.Shopify?.currency?.active || 'USD'
  }).format(cents / 100);
  const track = (eventName, payload) => window.PuzzlePupAnalytics?.track(eventName, payload);

  async function sectionHtml(sectionId) {
    const response = await fetch(`${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}`, {
      credentials: 'same-origin', headers: { Accept: 'text/html' }
    });
    if (!response.ok) throw new Error(`Could not refresh ${sectionId}`);
    return response.text();
  }

  function syncCartTriggerLabel() {
    const icon = document.getElementById('cart-icon-bubble');
    const trigger = icon?.closest('[data-pp-cart-trigger]');
    if (!trigger) return;
    const text = icon.querySelector('.cart-count-bubble .visually-hidden')?.textContent?.trim()
      || icon.querySelector('.visually-hidden')?.textContent?.trim() || '';
    const match = text.match(/\d+/);
    const count = match ? Number(match[0]) : 0;
    trigger.setAttribute('aria-label', `Cart, ${count} ${count === 1 ? 'item' : 'items'}`);
  }

  async function refreshCartSurfaces() {
    const drawer = document.querySelector('cart-drawer');
    const funnelDrawer = document.querySelector('[data-pp-funnel-cart-drawer]');
    const funnelWasOpen = funnelDrawer?.getAttribute('aria-hidden') === 'false';
    const icon = document.getElementById('cart-icon-bubble');
    const cartFooter = document.querySelector('[data-pp-cart-region]');
    const cartItems = document.getElementById('main-cart-items');
    const ids = [];
    if (drawer) ids.push('cart-drawer');
    if (funnelDrawer) ids.push('pp-funnel-cart-drawer');
    if (icon) ids.push('cart-icon-bubble');
    if (cartFooter) ids.push(cartFooter.dataset.sectionId);
    if (cartItems) ids.push(cartItems.dataset.id);
    const unique = [...new Set(ids.filter(Boolean))];
    await Promise.all(unique.map(async (id) => {
      const html = await sectionHtml(id);
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      if (id === 'cart-drawer' && drawer) {
        const next = parsed.querySelector('cart-drawer');
        const current = document.querySelector('cart-drawer');
        if (next && current) {
          const wasOpen = current.classList.contains('active');
          current.replaceWith(next);
          bindCartDrawer(next);
          if (wasOpen) next.open?.();
        }
      } else if (id === 'pp-funnel-cart-drawer' && funnelDrawer) {
        const next = parsed.querySelector('[data-pp-funnel-cart-drawer]');
        if (next) funnelDrawer.replaceWith(next);
      } else if (id === 'cart-icon-bubble' && icon) {
        const next = parsed.getElementById('cart-icon-bubble') || parsed.getElementById('shopify-section-cart-icon-bubble');
        if (next) {
          icon.innerHTML = next.innerHTML;
          syncCartTriggerLabel();
        }
      } else if (cartItems && id === cartItems.dataset.id) {
        const next = parsed.querySelector('cart-items');
        const current = cartItems.closest('cart-items');
        if (next && current) current.replaceWith(next);
      } else if (cartFooter) {
        const next = parsed.querySelector('[data-pp-cart-region]');
        if (next) cartFooter.replaceWith(next);
      }
    }));
    window.PuzzlePupCarousels?.init(document);
    bindCartDrawer();
    if (funnelWasOpen) window.PuzzlePupFunnelDrawer?.open?.();
    document.dispatchEvent(new CustomEvent('pp:cart-updated'));
  }

  let nativeCartRefreshTimer = null;
  let nativeCartRefreshRunning = false;
  let nativeCartRefreshAgain = false;
  let nativeCartUpdatesBound = false;

  function scheduleNativeCartRefresh() {
    if (nativeCartRefreshRunning) {
      nativeCartRefreshAgain = true;
      return;
    }
    window.clearTimeout(nativeCartRefreshTimer);
    nativeCartRefreshTimer = window.setTimeout(async () => {
      nativeCartRefreshRunning = true;
      try {
        // Shrine's drawer clear emits its own cart-update subscription only.
        // Its other cart ignores the shared cart-items source, leaving /cart stale.
        await refreshCartSurfaces();
      } catch (error) {
        console.error('PuzzlePup cart display refresh failed:', error);
      } finally {
        nativeCartRefreshRunning = false;
        if (nativeCartRefreshAgain) {
          nativeCartRefreshAgain = false;
          scheduleNativeCartRefresh();
        }
      }
    }, 180);
  }

  function bindNativeCartUpdates() {
    if (nativeCartUpdatesBound || typeof subscribe !== 'function'
      || typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS.cartUpdate) return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, scheduleNativeCartRefresh);
    nativeCartUpdatesBound = true;
  }

  document.addEventListener('cartQuantityUpdated', scheduleNativeCartRefresh);
  document.addEventListener('DOMContentLoaded', bindNativeCartUpdates, { once: true });
  bindNativeCartUpdates();

  function bindCartDrawer(drawer = document.querySelector('cart-drawer')) {
    if (!drawer) return null;
    const needsFallback = typeof drawer.open !== 'function' || typeof drawer.close !== 'function';

    if (typeof drawer.open !== 'function') {
      drawer.open = function (triggeredBy) {
        this.ppTriggeredBy = triggeredBy || document.activeElement;
        this.classList.add('animate', 'active');
        document.body.classList.add('overflow-hidden');
        const dialog = this.querySelector('[role="dialog"]');
        dialog?.focus({ preventScroll: true });
      };
    }

    if (typeof drawer.close !== 'function') {
      drawer.close = function () {
        this.classList.remove('active');
        document.body.classList.remove('overflow-hidden');
        if (this.ppTriggeredBy?.focus) this.ppTriggeredBy.focus({ preventScroll: true });
      };
    }

    if (needsFallback && drawer.dataset.ppDrawerBound !== 'true') {
      drawer.dataset.ppDrawerBound = 'true';
      drawer.dataset.ppDrawerFallback = 'true';
      drawer.querySelector('.cart-drawer__overlay')?.addEventListener('click', () => drawer.close());
      drawer.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && drawer.classList.contains('active')) drawer.close();
      });
    }

    return drawer;
  }

  function openCartDrawer(triggeredBy) {
    const themeDrawer = bindCartDrawer();
    if (typeof themeDrawer?.open === 'function') {
      themeDrawer.open(triggeredBy);
      return true;
    }
    if (typeof window.PuzzlePupFunnelDrawer?.open === 'function') {
      window.PuzzlePupFunnelDrawer.open();
      return true;
    }
    return false;
  }

  async function addItems(items) {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items })
    });
    const payload = await response.json();
    if (!response.ok) throw new Error(payload.description || payload.message || 'This item could not be added.');
    return payload;
  }

  document.addEventListener('click', async (event) => {
    const cartTrigger = event.target.closest('[data-pp-cart-trigger], a.header__icon--cart');
    if (cartTrigger && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
      const drawer = bindCartDrawer();
      if (drawer?.dataset.ppDrawerFallback === 'true') {
        event.preventDefault();
        openCartDrawer(cartTrigger);
        return;
      }
    }

    const quizLink = event.target.closest('a[href*="/pages/dog-supplement-quiz"]');
    if (quizLink) {
      track('pp_quiz_click', {
        link_text: quizLink.textContent?.trim(),
        destination: quizLink.getAttribute('href'),
        source_section: quizLink.closest('section')?.className || 'site_navigation'
      });
    }
    const scrollButton = event.target.closest('[data-pp-scroll-to-buy]');
    if (scrollButton) {
      event.preventDefault();
      document.querySelector('[data-pp-product-form]')?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
      return;
    }
    const button = event.target.closest('[data-pp-add-variant]');
    if (!button) return;
    event.preventDefault();
    if (button.disabled) return;
    const variantId = Number(button.dataset.ppAddVariant);
    const quantity = Number(button.dataset.ppQuantity || 1);
    const feedback = button.closest('[data-pp-add-context]')?.querySelector('[data-pp-add-feedback]');
    const originalLabel = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding…';
    if (feedback) { feedback.textContent = ''; feedback.className = 'pp-small'; }
    try {
      await addItems([{ id: variantId, quantity }]);
      track('pp_add_to_cart', {
        variant_id: variantId,
        quantity,
        purchase_type: 'one-time',
        source_section: button.closest('section')?.className || 'storefront'
      });
      button.textContent = 'Added';
      if (feedback) { feedback.textContent = 'Added to your cart.'; feedback.className = 'pp-success'; }
      await refreshCartSurfaces();
      openCartDrawer();
    } catch (error) {
      button.textContent = originalLabel;
      if (feedback) { feedback.textContent = error.message; feedback.className = 'pp-error'; }
    } finally {
      window.setTimeout(() => { button.disabled = false; if (button.textContent === 'Added') button.textContent = originalLabel; }, 1000);
    }
  });

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-pp-variant-select]');
    if (!select) return;
    const option = select.selectedOptions[0];
    const context = select.closest('[data-pp-buy-panel]');
    const button = context?.querySelector('[data-pp-add-variant]');
    const price = context?.querySelector('[data-pp-live-price]');
    if (button) button.dataset.ppAddVariant = option.value;
    if (price) price.textContent = option.dataset.priceFormatted || money(Number(option.dataset.price || 0));
    const stickyPrice = document.querySelector('[data-pp-sticky-price]');
    if (stickyPrice) stickyPrice.textContent = option.dataset.priceFormatted || option.dataset.price || '';
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-pp-contact-form]');
    if (!form) return;
    const email = form.querySelector('[type=email]');
    const message = form.querySelector('textarea');
    if (!email?.value.trim() || !message?.value.trim()) {
      event.preventDefault();
      const feedback = form.querySelector('[data-pp-form-feedback]');
      if (feedback) feedback.textContent = 'Please enter your email and a message.';
    }
  });

  function ppLabelKachingControls(root = document) {
    root.querySelectorAll('.kaching-subscriptions__card-radio[role="button"]').forEach((control, index) => {
      const plan = control.closest('.kaching-subscriptions__selling-plan, .kaching-subscriptions__card');
      const planTitle = plan?.querySelector(
        '.kaching-subscriptions__selling-plan-title, .kaching-subscriptions__card-title, [class*="title"]'
      )?.textContent?.trim();
      control.setAttribute('aria-label', planTitle ? `Select ${planTitle}` : `Select purchase option ${index + 1}`);
    });
  }

  bindCartDrawer();
  syncCartTriggerLabel();
  ppLabelKachingControls();
  new MutationObserver((mutations) => {
    if (mutations.some((mutation) => mutation.addedNodes.length)) {
      syncCartTriggerLabel();
      ppLabelKachingControls();
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  window.PuzzlePupStorefront = Object.freeze({ addItems, refreshCartSurfaces, openCartDrawer });
})();

